import React, { forwardRef, Ref, useImperativeHandle } from "react";
import { makeStyles } from "@mui/styles";
import {
    Backdrop,
    Box,
    Button,
    IconButton,
    Modal,
    Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Swal from "sweetalert2";
import { useCancelUserSubscriptionMutation } from "services/webServer";
import { CONSTANTS } from "consts";
import { useHistory } from "react-router-dom";
import MembershipCanceledModal, {
    IMembershipCanceledModalRef,
} from "Screens/Memberships/Detail/CancelReasons/MembershipCanceledModal";
import useQueryParams from "Hooks/useQueryParams";

export interface ICancalOfferDowngradeMonthlyModalRef {
    isVisible: boolean;
    setIsVisible: (value: boolean) => void;
}

interface CancelOfferDowngradeMonthlyModalProps {}

const useStyles: any = makeStyles({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

const CancelOfferDowngradeMonthlyModal = (
    props: CancelOfferDowngradeMonthlyModalProps,
    ref: Ref<ICancalOfferDowngradeMonthlyModalRef>
) => {
    const { id = "" } = useQueryParams();
    const history = useHistory();
    const [isVisible, setIsVisible] = React.useState<boolean>(false);
    const classes = useStyles();
    const membershipCancelSuccessRef =
        React.useRef<IMembershipCanceledModalRef>(null);

    const [cancelUserSubscription] = useCancelUserSubscriptionMutation({
        onCompleted: () => {
            sessionStorage.removeItem("cancelReason");
            membershipCancelSuccessRef.current?.setIsVisible(true);
        },
        onError: () => {
            history.replace(CONSTANTS.NAVIGATION_URL.MEMBERSHIPS);
        },
    });

    useImperativeHandle(
        ref,
        () => ({
            isVisible,
            setIsVisible,
        }),
        [isVisible]
    );

    const handleClose = () => {
        setIsVisible(false);
    };

    const handleChooseCancel = async () => {
        handleClose();
        const reasonData = JSON.parse(
            sessionStorage.getItem("cancelReason") as string
        );
        Swal.showLoading();
        await cancelUserSubscription({
            variables: {
                id,
                reason: reasonData?.reason || "Other",
                reasonDetail: reasonData?.reasonDetail || "",
            },
        });
        Swal.close();
    };

    const handleChoosRedeemOffer = () => {
        history.push(
            `${CONSTANTS.NAVIGATION_URL.MEMBERSHIP_REDEEM_OFFER_DOWNGRADE_TO_MONTHLY}?id=${id}`
        );
    };

    return (
        <>
            <Modal
                open={isVisible}
                onClose={handleClose}
                className={classes.modal}
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                closeAfterTransition
                BackdropComponent={Backdrop}
            >
                <Box
                    sx={{
                        outline: "none",
                        backgroundColor: "white",
                        borderRadius: "16px",
                        width: {
                            xs: "96%",
                            sm: "80%",
                            md: "600px",
                        },
                        zIndex: 2,
                        position: "relative",
                        padding: "48px 32px",
                    }}
                >
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            top: "5px",
                            right: "10px",
                        }}
                    >
                        <Close />
                    </IconButton>
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: {
                                    xs: "20px",
                                    md: "32px",
                                },
                                lineHeight: {
                                    xs: "20px",
                                    md: "32px",
                                },
                                letterSpacing: "0.01em",
                                color: "#161616",
                                marginBottom: "16px",
                                fontFamily: "GRIFTER",
                            }}
                        >
                          WAIT! GET 50% OFF
                         </Typography>
                         <Typography
                            sx={{
                              fontFamily: "Work Sans",
                              fontWeight: 400,
                              fontSize: "16px",
                              lineHeight: "20px",
                              color: "#000000",
                              marginBottom: "24px",
                            }}
                         >
                            Still thinking about canceling? Enjoy 2 months at just $17/month.
                         </Typography>
                        <Button
                            sx={{
                                width: "100%",
                                backgroundColor: "#FF4106",
                                color: "#FFFFFF",
                                fontWeight: 700,
                                fontSize: "16px",
                                "&:hover": {
                                    backgroundColor: "#FF4106",
                                },
                            }}
                            onClick={handleChoosRedeemOffer}
                        >
                            Redeem Offer
                        </Button>
                        <Button
                            sx={{
                                width: "100%",
                                backgroundColor: "transparent",
                                color: "#000",
                                fontWeight: 500,
                                fontSize: "14px",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            }}
                            onClick={handleChooseCancel}
                        >
                            No Thanks, Cancel Anyway
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <MembershipCanceledModal ref={membershipCancelSuccessRef} />
        </>
    );
};

export default forwardRef(CancelOfferDowngradeMonthlyModal);
